import { Component } from '@theme/component';

const EASINGS = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  gentle: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  linear: 'linear',
};

/**
 * Announcement banner custom element with configurable transition modes.
 *
 * Motion is bounded to the announcement text, pauses while hidden/offscreen or
 * while a visitor is interacting with it, and falls back to an immediate state
 * change when reduced motion is requested.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #activeIndex = 0;
  #timer = undefined;
  #activeAnimations = [];
  #transitionToken = 0;
  #intersectionObserver = undefined;
  #pauseReasons = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (document.hidden) this.#pauseReasons.add('document-hidden');

    this.#initializeSlideState();
    this.#prepareTypewriterCopies();
    this.#observeVisibility();

    requestAnimationFrame(async () => {
      await this.#runInitialEffect();
      if (this.isConnected) this.play();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#transitionToken += 1;
    this.suspend();
    this.#cancelAnimations();
    this.#pauseReasons.clear();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - Time between announcements in milliseconds.
   */
  play(interval = this.#holdInterval) {
    if (!this.isConnected || !this.autoplay || this.paused || this.#pauseReasons.size > 0) return;

    this.suspend();
    this.#timer = window.setTimeout(async () => {
      this.#timer = undefined;
      this.#current += 1;
      await this.#show(this.#normalizeIndex(this.#current));
      this.play();
    }, Math.max(0, interval ?? 0));
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend = () => {
    window.clearTimeout(this.#timer);
    this.#timer = undefined;
  };

  resume = () => {
    if (this.paused) return;
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval) && (this.refs.slides?.length ?? 0) > 1;
  }

  get autoplayInterval() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(seconds) ? undefined : seconds * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    void this.#goTo(current);
  }

  get #mode() {
    return this.dataset.animationMode || 'fade';
  }

  get #duration() {
    return this.#readNumber('transitionDuration', 500);
  }

  get #isTravelMode() {
    return this.#mode === 'horizontal' || this.#mode === 'vertical';
  }

  get #usesPullFlow() {
    return this.#isTravelMode && this.dataset.travelFlow === 'pull';
  }

  get #holdInterval() {
    if (this.#isTravelMode) {
      return Math.max(0, this.#readNumber('middleDelay', 2)) * 1000;
    }

    return this.autoplayInterval;
  }

  get #easing() {
    return EASINGS[this.dataset.motionEasing] || EASINGS.smooth;
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #readNumber(key, fallback) {
    const value = Number.parseFloat(this.dataset[key] ?? '');
    return Number.isFinite(value) ? value : fallback;
  }

  #normalizeIndex(index) {
    const count = this.refs.slides?.length ?? 0;
    if (count === 0) return 0;

    const normalized = index % count;
    return normalized < 0 ? normalized + count : normalized;
  }

  #initializeSlideState() {
    for (const [index, slide] of (this.refs.slides ?? []).entries()) {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.removeAttribute('data-exiting');
    }
  }

  async #goTo(index) {
    this.suspend();
    this.#current = index;
    await this.#show(this.#normalizeIndex(index));
    this.play();
  }

  async #show(nextIndex) {
    const slides = this.refs.slides ?? [];
    const incoming = slides[nextIndex];
    const outgoing = slides[this.#activeIndex];

    if (!incoming || incoming === outgoing) return;

    const token = ++this.#transitionToken;
    this.#cancelAnimations();
    this.#removeTemporaryLayers();

    for (const slide of slides) {
      slide.removeAttribute('data-exiting');
    }

    if (this.#mode === 'typewriter') {
      this.#resetTypewriter(incoming);
    }

    outgoing?.setAttribute('aria-hidden', 'true');
    outgoing?.setAttribute('data-exiting', '');

    this.#activeIndex = nextIndex;

    if (this.#prefersReducedMotion) {
      incoming.setAttribute('aria-hidden', 'false');
      this.#finishTransition(incoming);
      return;
    }

    if (this.#isTravelMode) {
      incoming.setAttribute('aria-hidden', 'false');

      if (this.#usesPullFlow) {
        this.#activeAnimations = [
          ...(outgoing ? this.#createTravelExitAnimations(outgoing) : []),
          ...this.#createEntranceAnimations(incoming),
        ];
        await Promise.allSettled(this.#activeAnimations.map((animation) => animation.finished));

        if (token !== this.#transitionToken) return;

        this.#finishTransition(incoming);
        return;
      }

      if (outgoing) {
        this.#activeAnimations = this.#createTravelExitAnimations(outgoing);
        await Promise.allSettled(this.#activeAnimations.map((animation) => animation.finished));

        if (token !== this.#transitionToken) return;

        this.#cancelAnimations();
        outgoing.removeAttribute('data-exiting');
      }

      this.#activeAnimations = this.#createEntranceAnimations(incoming);
      await Promise.allSettled(this.#activeAnimations.map((animation) => animation.finished));

      if (token !== this.#transitionToken) return;

      this.#finishTransition(incoming);
      return;
    }

    incoming.setAttribute('aria-hidden', 'false');
    this.#activeAnimations = this.#createTransitionAnimations(outgoing, incoming);
    await Promise.allSettled(this.#activeAnimations.map((animation) => animation.finished));

    if (token !== this.#transitionToken) return;

    this.#finishTransition(incoming);
  }

  async #runInitialEffect() {
    const firstSlide = this.refs.slides?.[this.#activeIndex];
    if (!this.isConnected || !firstSlide || this.#prefersReducedMotion) return;

    const token = ++this.#transitionToken;
    this.#cancelAnimations();
    this.#removeTemporaryLayers();

    if (this.#mode === 'typewriter') {
      this.#resetTypewriter(firstSlide);
    }

    this.#activeAnimations = this.#createEntranceAnimations(firstSlide);
    await Promise.allSettled(this.#activeAnimations.map((animation) => animation.finished));

    if (token !== this.#transitionToken) return;

    this.#finishTransition(firstSlide);
  }

  #createTravelExitAnimations(slide) {
    return [
      slide.animate(
        [
          { opacity: 1, transform: 'translate3d(0, 0, 0)' },
          { opacity: 1, transform: this.#travelTransform('exit') },
        ],
        { duration: this.#duration, easing: this.#easing, fill: 'both' }
      ),
    ];
  }

  #travelTransform(stage) {
    if (this.#mode === 'horizontal') {
      const movesLeft = this.dataset.horizontalDirection !== 'right';
      const fromEdge = movesLeft ? 'calc(50vw + 50%)' : 'calc(-50vw - 50%)';
      const toEdge = movesLeft ? 'calc(-50vw - 50%)' : 'calc(50vw + 50%)';
      return `translate3d(${stage === 'enter' ? fromEdge : toEdge}, 0, 0)`;
    }

    const movesUp = this.dataset.verticalDirection !== 'down';
    const fromEdge = movesUp ? '100%' : '-100%';
    const toEdge = movesUp ? '-100%' : '100%';
    return `translate3d(0, ${stage === 'enter' ? fromEdge : toEdge}, 0)`;
  }

  #createTransitionAnimations(outgoing, incoming) {
    const duration = this.#duration;
    const timing = { duration, easing: this.#easing, fill: 'both' };
    const animations = [];

    switch (this.#mode) {
      case 'split_join':
        if (outgoing) {
          animations.push(
            outgoing.animate([{ opacity: 1 }, { opacity: 0 }], {
              duration: Math.min(duration * 0.55, 320),
              easing: this.#easing,
              fill: 'both',
            })
          );
        }
        animations.push(...this.#createSplitAnimations(incoming));
        break;
      case 'typewriter':
        if (outgoing) {
          animations.push(
            outgoing.animate([{ opacity: 1 }, { opacity: 0 }], {
              duration: Math.min(duration * 0.4, 220),
              easing: EASINGS.smooth,
              fill: 'both',
            })
          );
        }
        animations.push(...this.#createTypewriterAnimations(incoming));
        break;
      case 'zoom': {
        const startScale = this.#readNumber('zoomScale', 85) / 100;
        if (outgoing) {
          animations.push(
            outgoing.animate(
              [
                { opacity: 1, transform: 'scale(1)' },
                { opacity: 0, transform: `scale(${2 - startScale})` },
              ],
              timing
            )
          );
        }
        animations.push(
          incoming.animate(
            [
              { opacity: 0, transform: `scale(${startScale})` },
              { opacity: 1, transform: 'scale(1)' },
            ],
            timing
          )
        );
        break;
      }
      case 'fade':
      default:
        if (outgoing) {
          animations.push(outgoing.animate([{ opacity: 1 }, { opacity: 0 }], timing));
        }
        animations.push(incoming.animate([{ opacity: 0 }, { opacity: 1 }], timing));
        break;
    }

    return animations;
  }

  #createEntranceAnimations(slide) {
    const duration = this.#duration;
    const timing = { duration, easing: this.#easing, fill: 'both' };

    switch (this.#mode) {
      case 'horizontal': {
        return [
          slide.animate(
            [
              { opacity: 1, transform: this.#travelTransform('enter') },
              { opacity: 1, transform: 'translate3d(0, 0, 0)' },
            ],
            timing
          ),
        ];
      }
      case 'vertical': {
        return [
          slide.animate(
            [
              { opacity: 1, transform: this.#travelTransform('enter') },
              { opacity: 1, transform: 'translate3d(0, 0, 0)' },
            ],
            timing
          ),
        ];
      }
      case 'split_join':
        return this.#createSplitAnimations(slide);
      case 'typewriter':
        return this.#createTypewriterAnimations(slide);
      case 'zoom': {
        const startScale = this.#readNumber('zoomScale', 85) / 100;
        return [
          slide.animate(
            [
              { opacity: 0, transform: `scale(${startScale})` },
              { opacity: 1, transform: 'scale(1)' },
            ],
            timing
          ),
        ];
      }
      case 'fade':
      default:
        return [slide.animate([{ opacity: 0 }, { opacity: 1 }], timing)];
    }
  }

  #createSplitAnimations(slide) {
    const source = slide.querySelector('.announcement-bar__text');
    if (!source) return [];

    const distance = this.#readNumber('splitDistance', 40);
    const leftLayer = this.#createEffectLayer(slide, source, 'announcement-bar__split-half');
    const rightLayer = this.#createEffectLayer(slide, source, 'announcement-bar__split-half');

    source.style.opacity = '0';

    leftLayer.dataset.temporary = '';
    rightLayer.dataset.temporary = '';

    const timing = { duration: this.#duration, easing: this.#easing, fill: 'both' };

    return [
      leftLayer.animate(
        [
          { clipPath: 'inset(0 50% 0 0)', opacity: 0, transform: `translate3d(${-distance}%, 0, 0)` },
          { clipPath: 'inset(0 50% 0 0)', opacity: 1, transform: 'translate3d(0, 0, 0)' },
        ],
        timing
      ),
      rightLayer.animate(
        [
          { clipPath: 'inset(0 0 0 50%)', opacity: 0, transform: `translate3d(${distance}%, 0, 0)` },
          { clipPath: 'inset(0 0 0 50%)', opacity: 1, transform: 'translate3d(0, 0, 0)' },
        ],
        timing
      ),
    ];
  }

  #prepareTypewriterCopies() {
    if (this.#mode !== 'typewriter') return;

    for (const slide of this.refs.slides ?? []) {
      const source = slide.querySelector('.announcement-bar__text');
      if (!source || slide.querySelector('[data-typewriter-layer]')) continue;

      const layer = this.#createEffectLayer(slide, source, 'announcement-bar__typewriter-copy');
      layer.dataset.typewriterLayer = '';

      const copy = layer.querySelector('.announcement-bar__typewriter-copy');
      if (!copy) continue;

      copy.dataset.cursor = this.dataset.typewriterCursor || 'true';
      this.#wrapTextCharacters(copy);
      source.style.opacity = '0';
    }
  }

  #createEffectLayer(slide, source, copyClass) {
    const layer = document.createElement('div');
    const copy = source.cloneNode(true);

    layer.className = 'announcement-bar__effect-layer';
    layer.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('id');
    copy.classList.add(copyClass);
    layer.append(copy);
    slide.append(layer);

    return layer;
  }

  #wrapTextCharacters(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    for (const textNode of textNodes) {
      const fragment = document.createDocumentFragment();

      for (const character of textNode.textContent ?? '') {
        const span = document.createElement('span');
        span.className = 'announcement-bar__typewriter-char';
        span.textContent = character;
        fragment.append(span);
      }

      textNode.replaceWith(fragment);
    }
  }

  #resetTypewriter(slide) {
    for (const character of slide.querySelectorAll('.announcement-bar__typewriter-char')) {
      character.style.opacity = '0';
    }
  }

  #createTypewriterAnimations(slide) {
    const characters = [...slide.querySelectorAll('.announcement-bar__typewriter-char')];
    if (characters.length === 0) return [];

    const requestedDelay = this.#readNumber('typewriterSpeed', 45);
    const availableTime = this.autoplayInterval
      ? Math.max(300, this.autoplayInterval - Math.min(this.#duration, 600))
      : Number.POSITIVE_INFINITY;
    const fittedDelay = Number.isFinite(availableTime)
      ? availableTime / Math.max(characters.length, 1)
      : requestedDelay;
    const characterDelay = Math.max(8, Math.min(requestedDelay, fittedDelay));
    const characterDuration = Math.max(90, Math.min(220, characterDelay * 2.2));

    return characters.map((character, index) =>
      character.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: characterDuration,
        delay: index * characterDelay,
        easing: 'linear',
        fill: 'both',
      })
    );
  }

  #finishTransition(incoming) {
    if (this.#mode === 'typewriter') {
      for (const character of incoming.querySelectorAll('.announcement-bar__typewriter-char')) {
        character.style.opacity = '1';
      }
    }

    const incomingText = incoming.querySelector('.announcement-bar__text');
    if (incomingText && this.#mode !== 'typewriter') {
      incomingText.style.opacity = '';
    }

    for (const slide of this.refs.slides ?? []) {
      slide.removeAttribute('data-exiting');
    }

    this.#removeTemporaryLayers();
    this.#cancelAnimations();
  }

  #removeTemporaryLayers() {
    for (const layer of this.querySelectorAll('.announcement-bar__effect-layer[data-temporary]')) {
      layer.remove();
    }

    for (const slide of this.refs.slides ?? []) {
      if (this.#mode !== 'typewriter') {
        slide.querySelector('.announcement-bar__text')?.style.removeProperty('opacity');
      }
    }
  }

  #cancelAnimations() {
    for (const animation of this.#activeAnimations) {
      animation.cancel();
    }
    this.#activeAnimations = [];
  }

  #observeVisibility() {
    if (!('IntersectionObserver' in window)) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#setPauseReason('offscreen', !(entry?.isIntersecting ?? true));
      },
      { threshold: 0.01 }
    );

    this.#intersectionObserver.observe(this);
  }

  #setPauseReason(reason, shouldPause) {
    if (shouldPause) {
      this.#pauseReasons.add(reason);
      this.suspend();
      return;
    }

    this.#pauseReasons.delete(reason);
    this.resume();
  }

  #handleFocusIn = () => {
    this.#setPauseReason('focus', true);
  };

  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#setPauseReason('focus', false);
  };

  #handleVisibilityChange = () => {
    this.#setPauseReason('document-hidden', document.hidden);
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
